import React, { Fragment } from "react";
import styled from "styled-components";
import Teaser from "./Teaser";
import PromotionSteps from "./PromotionSteps";
import { colors, font, spacing } from "../theme";
import BookmarkButton from "../bookmarks/BookmarkButton";
import { ApiResult } from "../../lib/CmsApi";
import TopBox from "../common/TopBox";
import { RouteName, RouteParams } from "../../lib/routes";
import DefaultSectionListWithNav, {
  flattenCMSSectionLists
} from "../sections/SectionListWithNav";
import { slug } from "../../lib/formatters";
import ContactsSection from "../sections/Contacts";
import DownloadSection from "../sections/Downloads";
import {
  StickyNav,
  extractLinksFromSections
} from "../sections/SectionListWithNav/StickyNav";
import RelatedNews from "../common/RelatedNews";
import { DEFAULT_PADDING } from "../common/Content";
import CustomPageHead from "../common/CustomPageHead";
import FundingStoriesSection from "../sections/FundingStories";

const SectionListWithNav = styled(DefaultSectionListWithNav)`
  .richtext > h1:first-child {
    margin: 0 0 1.7em 0;
  }
  section {
    &:not(:last-child) {
      padding-bottom: 0 !important;
      ${spacing("margin-bottom", 48)}
    }
    &:last-child {
      ${spacing("margin-bottom", 120)}
    }
  }
`;

const Teasers = styled.div`
  width: 100%;
  @media print {
    padding-left: calc(${DEFAULT_PADDING} / 2);
    padding-bottom: calc(${DEFAULT_PADDING} / 2);
  }
`;
const StyledTopBox = styled(TopBox)``;
const StyledBookmarkButton = styled(BookmarkButton)`
  ${spacing("margin-bottom", 64)};
`;
const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
const List = styled(FlexColumn.withComponent("ul"))`
  margin: 0;
  padding: 0;
  align-self: flex-end;
  justify-self: flex-start;
  list-style-type: none;
  ${spacing("padding-bottom", 48)};
  width: 100%;
`;

const ListItem = styled.li`
  font-weight: ${font.weightMedium};
  position: relative;
  padding-left: 20px;
  &:not(:last-child) {
    ${spacing("margin-bottom", 12)}
  }

  &:before {
    content: "•";
    left: 0;
    position: absolute;
    color: ${colors.pigeonGreyLight};
  }
`;
const Navigable = styled.div`
  position: relative;
`;

export type Props = ApiResult<"loadProgramBySlug"> & {
  url: string;
  relatedPortraits?: ApiResult<"listMagazinPortraits">;
  breadCrumpPath: { route: RouteName; params?: RouteParams; title: string }[];
};

export default function ProgramPage(props: Props) {
  const cmsSections = flattenCMSSectionLists(props.sections);
  const downloadLink = {
    navLink: "Downloads",
    id: slug("Downloads")
  };
  const contactLink = {
    navLink: "Ansprechpartner",
    id: slug("Ansprechpartner")
  };
  return (
    <Fragment>
      <CustomPageHead
        title={props.name}
        desc={props.abstract}
        seo={props.seo}
      />
      <StyledTopBox
        linearBackground
        title={props.name}
        overline={props.type && props.type.name}
        infoBox={
          <List>
            {props.keyFacts &&
              props.keyFacts.map((fact, idx) => (
                <ListItem key={idx}>{fact}</ListItem>
              ))}
          </List>
        }
        background
        breadCrumpPath={props.breadCrumpPath}
        noMargin
      />
      <Navigable>
        <StickyNav
          links={[
            ...extractLinksFromSections(cmsSections),
            props.contacts && !!props.contacts.length && contactLink,
            props.documents && !!props.documents.length && downloadLink
          ]}
        />
        <SectionListWithNav
          sections={cmsSections}
          noNavRender
          alwaysRenderSideBar
          limitContentWidth
          sidebarLabel="Weitere Links"
          sidebar={() => (
            <>
              <StyledBookmarkButton
                bookmark={{
                  id: props._id,
                  title: props.name,
                  href: props.url
                }}
                activeText="Artikel gemerkt"
              >
                Artikel merken
              </StyledBookmarkButton>
              {props.teasers && (
                <Teasers>
                  {props.teasers.map((t, idx) => (
                    <Teaser key={idx} {...t} />
                  ))}
                </Teasers>
              )}
            </>
          )}
        />
        <aside aria-label="Weitere Informationen">
          {props.promotionSteps && <PromotionSteps {...props.promotionSteps} />}
          {props.contacts && props.contacts.length > 0 && (
            <ContactsSection
              sectionProps={{
                id: contactLink.id,
                leftIndented: true
              }}
              alwaysMobileControls
              small
              contacts={props.contacts}
              headline="Wir sind für Sie da:"
              copy="Rufen Sie uns an oder schreiben Sie uns. Wir beantworten gerne Ihre Fragen und informieren Sie über die Angebote der IFB Hamburg."
              ariaLabel="Anprechpartner für ein Beratungsgespräch"
            />
          )}
          {props.documents.length > 0 && (
            <DownloadSection
              sectionProps={{
                id: downloadLink.id,
                leftIndented: true,
                color: colors.paleGrey
              }}
              headline="Downloads zu dieser Förderung"
              documents={props.documents}
            />
          )}
        </aside>
      </Navigable>
      {props.relatedPortraits &&
        props.relatedPortraits.items &&
        props.relatedPortraits.items.length > 0 && (
          <FundingStoriesSection
            headlineLabel={"Das könnte Sie auch interessieren"}
            items={props.relatedPortraits.items}
            sectionProps={{ reducedSectionPadding: true, leftIndented: true }}
          />
        )}
    </Fragment>
  );
}

ProgramPage.getImages = (props: Props) => {
  let images = [];
  const sections = flattenCMSSectionLists(props.sections);
  if (sections) {
    images = images.concat(DefaultSectionListWithNav.getImages({ sections }));
  }
  if (props.teasers) {
    props.teasers.forEach(t => {
      images = images.concat(Teaser.getImages(t));
    });
  }
  if (props.promotionSteps) {
    images = images.concat(PromotionSteps.getImages(props.promotionSteps));
  }
  images = images.concat(
    ContactsSection.getImages({ contacts: props.contacts })
  );
  if (props.relatedPortraits) {
    images = images.concat(
      props.relatedPortraits.items.map(n => n.headerImage).filter(Boolean)
    );
  }
  return images;
};
